import React, { useState } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getAttribution } from '../utils/attribution';

export default function QuoteForm() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: 'residential',
    heardAboutUs: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        ...getAttribution(),
        submittedFrom: window.location.pathname,
      };
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, payload);
      toast.success(t.quote.success, {
        description: t.quote.success_desc
      });
      setFormData({ name: '', email: '', phone: '', serviceType: 'residential', heardAboutUs: '', message: '' });
    } catch (error) {
       toast.error(t.quote.error, {
         description: t.quote.error_desc
       });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-20 bg-neutral-900 text-white border-t border-neutral-800">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">{t.quote.title}</h2>
            <p className="text-lg text-neutral-400 mb-8">
              {t.quote.subtitle}
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-red-900/20 rounded-lg">
                  <Phone className="h-6 w-6 text-red-500" />
                </div>
                <a href="tel:5146534072" className="text-xl text-white hover:text-red-500 transition-colors font-medium">
                  (514) 653-4072
                </a>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-2 bg-red-900/20 rounded-lg">
                  <Mail className="h-6 w-6 text-red-500" />
                </div>
                <span className="text-xl text-white break-all">Paysagementacatalogna@gmail.com</span>
              </div>
            </div>
          </div>
          
          <Card className="bg-black text-white border border-neutral-800 shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl">{t.quote.form_title}</CardTitle>
              <CardDescription className="text-neutral-400">{t.quote.form_subtitle}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-neutral-300">{t.quote.name}</Label>
                    <Input 
                      id="name" 
                      data-testid="quote-name-input"
                      placeholder={t.quote.name_ph} 
                      required 
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      className="bg-neutral-900 border-neutral-800 focus-visible:ring-red-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-neutral-300">{t.quote.phone}</Label>
                    <Input 
                      id="phone" 
                      data-testid="quote-phone-input"
                      placeholder={t.quote.phone_ph} 
                      required 
                      value={formData.phone}
                      onChange={e => setFormData({...formData, phone: e.target.value})}
                      className="bg-neutral-900 border-neutral-800 focus-visible:ring-red-600"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-neutral-300">{t.quote.email}</Label>
                  <Input 
                    id="email" 
                    data-testid="quote-email-input"
                    type="email" 
                    placeholder={t.quote.email_ph} 
                    required 
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="bg-neutral-900 border-neutral-800 focus-visible:ring-red-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service" className="text-neutral-300">{t.quote.service}</Label>
                  <select 
                    id="service" 
                    className="flex h-10 w-full items-center justify-between rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-white"
                    value={formData.serviceType}
                    onChange={e => setFormData({...formData, serviceType: e.target.value})}
                  >
                    <option value="residential">{t.service_types.residential}</option>
                    <option value="commercial">{t.service_types.commercial}</option>
                    <option value="hardscape">{t.service_types.hardscape}</option>
                    <option value="maintenance">{t.service_types.maintenance}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="heard" className="text-neutral-300">{t.quote.heard}</Label>
                  <select
                    id="heard"
                    data-testid="quote-heard-select"
                    className="flex h-10 w-full items-center justify-between rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 text-white"
                    value={formData.heardAboutUs}
                    onChange={e => setFormData({...formData, heardAboutUs: e.target.value})}
                  >
                    <option value="">{t.quote.heard_ph}</option>
                    <option value="google">{t.sources.google}</option>
                    <option value="social">{t.sources.social}</option>
                    <option value="referral">{t.sources.referral}</option>
                    <option value="vehicle">{t.sources.vehicle}</option>
                    <option value="repeat">{t.sources.repeat}</option>
                    <option value="other">{t.sources.other}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-neutral-300">{t.quote.project}</Label>
                  <Textarea 
                    id="message" 
                    placeholder={t.quote.project_ph} 
                    className="min-h-[100px] bg-neutral-900 border-neutral-800 focus-visible:ring-red-600"
                    value={formData.message}
                    onChange={e => setFormData({...formData, message: e.target.value})}
                  />
                </div>
                <Button type="submit" data-testid="quote-submit-btn" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6 border-none" disabled={loading}>
                  {loading ? t.quote.sending : t.quote.submit}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
